import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { toast } from 'react-toastify';
import { CloseRounded } from '@mui/icons-material';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { Content, Section, SectionHeaderDrawer, Typography } from './styles';

import useAuthStore from '../../../store/auth';
import useCompanyStore from '../../../store/companies';

import { Company, signUpRequest, User } from '../../../services';

import {
  isValidCPF,
  removeDotsAndTracesCPF,
} from '../../../utils/cpfValidation';

import { Input } from '../../../components/Input';
import { CellphoneInput } from '../../../components/CellphoneInput';
import { CpfInput } from '../../../components/CpfInput';
import { LoadingBackdrop } from '../../../components/LoadingBackdrop';

interface CriarMembroProps {
  setOpenDrawerAddMember: (open: boolean) => void;
  openDrawerAddMember: boolean;
  listagemUsuarios: Partial<User[]>;
  setListagemUsuarios: any;
  loadCompany: () => any;
}

export function CriarMembro({
  setOpenDrawerAddMember,
  openDrawerAddMember,
  listagemUsuarios,
  setListagemUsuarios,
  loadCompany,
}: CriarMembroProps) {
  const selectedCompany = useCompanyStore(state => state.selectedCompany);
  const user = useAuthStore(state => state.user);

  const [permissionSelected, setPermissionSelected] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const formValidation = useCallback((): any => {
    return Yup.object().shape({
      name: Yup.string().required('O campo Nome é obrigatório.'),
      surname: Yup.string().required('O campo Sobrenome é obrigatório.'),
      email: Yup.string()
        .email('Digite um e-mail válido')
        .required('O campo E-mail é obrigatório.'),
      cellphone: Yup.string().nullable(),
      profile: Yup.string().required('O campo Perfil é obrigatório.'),
      cpf: Yup.string()
        .required('O campo CPF é obrigatório.')
        .min(11, 'Mínimo de 11 dígitos')
        .test('test-invalid-cpf', 'CPF inválido', cpf =>
          isValidCPF(cpf as string),
        ),
    });
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(formValidation()),
  });

  const handleProfileChange = (event: SelectChangeEvent) => {
    setPermissionSelected(event.target.value);
    setValue('profile', event.target.value);
  };

  const handleCloseDrawer = useCallback(() => {
    reset();
    setOpenDrawerAddMember(false);
  }, [reset, setOpenDrawerAddMember]);

  const handleSaveUser = useCallback(
    async (formData: any) => {
      setIsLoading(true);
      try {
        if (user?.profile === 'UN1' || user?.profile === 'UN2') {
          const companies = [selectedCompany as Company];
          const companies_ids = [selectedCompany?.id as string];

          const response = await signUpRequest({
            name: `${formData?.name?.trim()} ${formData?.surname?.trim()}`,
            email: formData.email,
            phone: formData.cellphone,
            profile: formData?.profile,
            companies,
            companies_ids,
            doc: removeDotsAndTracesCPF(formData.cpf),
            userId: user?.id as string,
          });

          if (response?.status === 201) {
            const newUser = {
              ...response?.data,
            };

            toast.success('Usuário convidado com sucesso');
            setListagemUsuarios([...listagemUsuarios, newUser]);
            setPermissionSelected('');
            handleCloseDrawer();
            loadCompany();
          } else {
            toast.error('Usuário já está cadastrado nessa empresa');
          }
        } else {
          toast.warning('Você não tem permissão para adicionar usuários');
        }
      } catch (error) {
        toast.error('Ocorreu um erro ao cadastrar o usuário');
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      user?.profile,
      user?.id,
      selectedCompany,
      setListagemUsuarios,
      listagemUsuarios,
      handleCloseDrawer,
      loadCompany,
    ],
  );

  useEffect(() => {
    if (openDrawerAddMember) {
      clearErrors('cpf');
    }
  }, [clearErrors, openDrawerAddMember]);

  return (
    <Box
      sx={{ width: '560px', height: '100%', display: 'flex' }}
      role="presentation"
    >
      {isLoading && (
        <LoadingBackdrop
          text="Adicionando usuário na empresa"
          component=""
          loading={isLoading}
        />
      )}
      <Content>
        <form onSubmit={handleSubmit(handleSaveUser)} className="form-login">
          <SectionHeaderDrawer className="header-guests">
            <CloseRounded
              sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
              onClick={handleCloseDrawer}
            />
            <Typography>Adicionar Usuário</Typography>
          </SectionHeaderDrawer>
          <Section>
            <Input
              label="Digite o nome"
              className="input"
              id="outlined-basic"
              name="name"
              type="text"
              control={control}
              required
            />

            <Input
              label="Digite o sobrenome"
              className="input"
              id="outlined-basic"
              name="surname"
              type="text"
              control={control}
              required
            />

            <Input
              label="Digite o e-mail"
              className="input"
              id="outlined-basic"
              name="email"
              control={control}
              required
            />

            <Input
              className="inputMember-cel"
              id="outlined-basic"
              label="Celular"
              name="cellphone"
              control={control}
              InputProps={{
                inputComponent: CellphoneInput as any,
              }}
            />

            <Input
              className="input"
              id="outlined-basic"
              label="CPF"
              name="cpf"
              control={control}
              InputProps={{
                inputComponent: CpfInput as any,
              }}
              required
            />

            <FormControl sx={{ width: '100%' }}>
              <InputLabel id="demo-simple-select-autowidth-label">
                Selecione o permissionamento
              </InputLabel>
              <Select
                labelId="demo-simple-select-autowidth-label"
                id="demo-simple-select-autowidth"
                value={permissionSelected}
                onChange={handleProfileChange}
                autoWidth
                label="Selecione o permissionamento"
              >
                <MenuItem value="UN2">Admin</MenuItem>
                <MenuItem value="UN3">Membro</MenuItem>
              </Select>
            </FormControl>
          </Section>
          <Box
            padding="0px 20px 20px 20px"
            display="flex"
            justifyContent="end"
            alignItems="end"
            height="100%"
          >
            <Button
              variant="contained"
              type="submit"
              color="success"
              disabled={isSubmitting}
              sx={{
                width: '197px',
                height: '42px',
                color: '#FFFFFF',
                fontSize: '15px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
              }}
            >
              Adicionar Usuário
            </Button>
          </Box>
        </form>
      </Content>
    </Box>
  );
}
